import { readCropFractions, readHotspotFractions } from '@coas/imgproxy/json';

export type ImageLoader = (params: { src: string; width: number; quality?: number }) => string;

export type MakeImgLoaderArgs = {
    src: string;
    width?: number | null;
    height?: number | null;
    quality?: number | null;
};

export interface ImageLike {
    key: string | null;
    width?: number | null;
    height?: number | null;
    crop?: unknown | null;
    hotspot?: unknown | null;
    lqip?: string | null;
    alt?: string | null;
}

const clamp = (n: number, lo: number, hi: number) => (n < lo ? lo : n > hi ? hi : n);
const toPosInt = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

export function createImgLoaderFactory(baseUrl: string, endpoint = '/api/image') {
    return ({ src, width: prefW, height: prefH, quality: prefQ }: MakeImgLoaderArgs): ImageLoader => {
        return ({ width, quality }) => {
            const wFromNext = toPosInt(width);
            const wFromModel = toPosInt(prefW ?? 0);
            const w = wFromNext || wFromModel;

            const hFromModel = toPosInt(prefH ?? 0);

            const qFromNext = toPosInt(typeof quality === 'number' ? quality : Number.NaN);
            const qDefault = toPosInt(prefQ ?? 75) || 75;
            const q = clamp(qFromNext || qDefault, 1, 100);

            const params = new URLSearchParams({ src, w: String(w), q: String(q) });
            if (hFromModel > 0) params.set('h', String(hFromModel));

            return `${baseUrl}${endpoint}?${params.toString()}`;
        };
    };
}

export function createProductImgLoaderFactory(baseUrl: string, endpoint = '/api/imageProduct') {
    return (image: ImageLike): ImageLoader => {
        return ({ width, quality }) => {
            const q = clamp(toPosInt(typeof quality === 'number' ? quality : Number.NaN) || 75, 1, 100);

            const crop = readCropFractions(image.crop ?? null);
            const hs = readHotspotFractions(image.hotspot ?? null);

            const params = new URLSearchParams({
                key: String(image.key ?? ''),
                ow: String(toPosInt(image.width ?? 0)),
                oh: String(toPosInt(image.height ?? 0)),
                w: String(toPosInt(width)),
                q: String(q),
                cl: String(crop.left),
                ct: String(crop.top),
                cr: String(crop.right),
                cb: String(crop.bottom),
                hsx: String(hs.x),
                hsy: String(hs.y),
                hsw: String(hs.width),
                hsh: String(hs.height),
            });

            return `${baseUrl}${endpoint}?${params.toString()}`;
        };
    };
}
